/**
 * Mobile Navigation Menu
 *
 * This component renders:
 *  - A hamburger toggle button
 *  - A full‑screen animated mobile navigation overlay
 *  - Rows of animated "sliding panels"
 *  - Navigation links with staggered transitions
 *  - A "Connect" action button
 *
 * Compatible with: React 18+, Node 18–24, Next.js 14+
 */

import { FC, MouseEventHandler, useCallback } from "react";
import clsx from "clsx";
import { Link } from "components/link";
import { HEADER } from "content/layout";
import type { Property } from "csstype";

// Explicit csstype mappings for CSS variables
type BorderWidth = Property.BorderWidth;
type Width = Property.Width;

type HeaderMobileNavProps = {
  /** Controls whether the mobile nav is visible */
  showNav: boolean;

  /** Setter to toggle the mobile navigation state */
  setShowNav: (value: boolean | ((prev: boolean) => boolean)) => void;
};

/**
 * Mobile hamburger icon button
 */
const MobileNavButton: FC<{
  active: boolean;
  border: BorderWidth;
  size: Width;
  onClick: MouseEventHandler<HTMLButtonElement>;
}> = ({ active, border, size, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={active}
      aria-label="Toggle Navigation Menu"
      className="relative z-50 p-1"
      style={{
        "--nav-icon-size": size,
        "--nav-icon-border": border,
      }}
    >
      <i className={clsx("burger-menu", active && "active")}>
        <div />
      </i>
    </button>
  );
};

/**
 * Mobile Navigation Wrapper
 */
export const MobileNav: FC<HeaderMobileNavProps> = ({ showNav, setShowNav }) => {
  const toggleNav = useCallback(() => {
    setShowNav((prev) => !prev);
  }, [setShowNav]);

  return (
    <div className="md:hidden">
      {/* Hamburger Button */}
      <MobileNavButton
        size="24px"
        border="2px"
        active={showNav}
        onClick={toggleNav}
      />

      {/* Navigation Overlay */}
      <div
        className={clsx(
          "fixed left-0 top-0 h-screen w-full",
          showNav
            ? "nav-active opacity-100"
            : "pointer-events-none select-none opacity-0 delay-[900ms]"
        )}
      >
        {/* Sliding background columns */}
        <div className="absolute inset-0 -z-50 grid grid-cols-[1rem_repeat(16,minmax(0,1fr))_1rem]">
          {Array.from({ length: 18 }).map((_, index) => (
            <div
              key={index}
              className={clsx(
                "pointer-events-none relative h-full select-none bg-slate-900 transition-all duration-300 ease-linear",
                index === 0 &&
                "before:absolute before:right-0 before:top-0 before:h-full before:w-px before:bg-[linear-gradient(180deg,var(--line-color),var(--line-color)_50%,transparent_0,transparent)] before:bg-[length:1px_8px] before:opacity-20",
                index === 17 &&
                "before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-[linear-gradient(180deg,var(--line-color),var(--line-color)_50%,transparent_0,transparent)] before:bg-[length:1px_8px] before:opacity-20"
              )}
              style={{
                transitionDelay: showNav
                  ? `${index * 0.01}s`
                  : `${index * 0.025}s`,
                "--tw-translate-y": showNav ? "0%" : "-100%",
              }}
            />
          ))}
        </div>

        {/* Content */}
        <section className="mt-28 p-8 spacing-24">
          <nav className="relative spacing-6 text-white">
            {HEADER.nav.map((navItem, index) => {
              const transitionBase = showNav
                ? `transform 0.15s ${0.4 + 0.05 * index}s`
                : `transform 0.15s ${0.05 * index}s`;

              return (
                <Link
                  key={navItem.href}
                  href={navItem.href}
                  onClick={() => setShowNav(false)}
                  className={clsx(
                    "group flex items-baseline justify-between py-2 text-gray-300",
                    "opacity-0 transition-opacity delay-200",
                    "[.nav-active_&]:opacity-100",
                    "hfa:text-sky-400"
                  )}
                >
                  <span
                    className="-translate-x-[200%] text-[17px] font-medium [.nav-active_&]:translate-x-0"
                    style={{ transition: transitionBase }}
                  >
                    {navItem.title}
                  </span>

                  <div className="mx-2 h-px flex-1 bg-[linear-gradient(90deg,var(--line-color),var(--line-color)_50%,transparent_0,transparent)] bg-[length:8px_1px] opacity-0 transition-opacity [.nav-active_&]:opacity-40 [.nav-active_&]:delay-500" />

                  <small
                    className="translate-x-[200%] text-gray-400 [.nav-active_&]:translate-x-0"
                    style={{ transition: transitionBase }}
                  >
                    {navItem.alt}
                  </small>
                </Link>
              );
            })}
          </nav>

          {/* Action Button */}
          <div
            className="
              flex translate-y-8 items-center justify-center opacity-0
              [.nav-active &]:translate-y-0 
              [.nav-active &]:opacity-100 
              [.nav-active &]:delay-500
            "
            style={{
              transition: showNav
                ? `transform 0.15s 0.35s, opacity 0.2s 0.35s`
                : `transform 0.15s 0.05s, opacity 0.2s 0.05s`,
            }}
          >
            <button
              className="
                button-rainbow 
                relative 
                rounded-none whitespace-nowrap
                border-[4px] border-opacity-40
                px-10 py-3 font-medium tracking-tight

                /* fix: dark glass interior, NOT bright white */
                bg-black/30

                /* fix: bright readable text */
                text-black

                backdrop-blur-sm
              "
            >
              Connect
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
